"use client";
import React from "react";

interface Option {
  label: string;
  value: string;
}

interface SwitchGroupProps {
  name?: string;
  options: Option[];
  selectedValues?: string[];
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  style?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  iconSize?: number;
  iconCheckedBgColor?: string;
  switchBgColor?: string;
  textColor?: string;
  errorStyle?: React.CSSProperties;
}

export const SwitchGroup: React.FC<SwitchGroupProps> = ({
  name,
  options,
  selectedValues = [],
  onChange,
  disabled = false,
  readOnly = false,
  required = false,
  error,
  className = "",
  style,
  labelStyle,
  iconSize = 20,
  iconCheckedBgColor = "#2563eb",
  switchBgColor = "#d1d5db",
  textColor = "#374151",
  errorStyle,
}) => {
  const handleChange = (value: string) => {
    if (!onChange) return;
    const updatedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(updatedValues);
  };

  return (
    <div
      className={className}
      style={{ display: "flex", flexDirection: "column", gap: 8, ...style }}
      role="group"
      aria-disabled={disabled}
    >
      {options.map((option) => {
        const isChecked = selectedValues.includes(option.value);
        return (
          <label
            key={option.value}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
              gap: 8,
              userSelect: "none",
              ...labelStyle,
            }}
          >
            <span style={{ color: textColor, fontSize: 14 }}>{option.label}</span>
            <input
              type="checkbox"
              name={name}
              value={option.value}
              checked={isChecked}
              disabled={disabled || readOnly}
              required={required}
              onChange={() => handleChange(option.value)}
              style={{ display: "none" }}
            />
            <span
              style={{
                position: "relative",
                width: iconSize * 2,
                height: iconSize * 1.1,
                borderRadius: 9999,
                backgroundColor: isChecked ? iconCheckedBgColor : switchBgColor,
                transition: "background-color 0.25s ease",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  left: isChecked
                    ? `calc(100% - ${iconSize - 4}px - 2px)`
                    : "2px",
                  transform: "translateY(-50%)",
                  width: iconSize - 4,
                  height: iconSize - 4,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  transition: "left 0.25s ease",
                }}
              />
            </span>
          </label>
        );
      })}
      {error && (
        <p
          role="alert"
          style={{
            color: "#dc2626",
            fontSize: 12,
            marginTop: 4,
            ...errorStyle,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};
